import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import EnquiryModal from "@/components/EnquiryModal";
import { api, resolveImageUrl } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  BadgeCheck,
  BedDouble,
  Compass,
  Home as HomeIcon,
  KeyRound,
  MapPin,
  Ruler,
  Sofa,
  Sparkles,
  TrendingUp,
  MessageCircle,
} from "lucide-react";
import { AssuredRentalBox, RentalBadge } from "@/components/AssuredRental";
import { isListingVisible } from "@/lib/featureFlags";

export default function PropertyDetail() {
  const { id } = useParams();
  const { lang, t } = useLang();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const [siblings, setSiblings] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/properties/${id}`);
        if (!isListingVisible(data)) return;
        setProperty(data);
        if (data.project_slug) {
          const res = await api.get(`/projects/${data.project_slug}`);
          setSiblings(res.data.units);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading)
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 py-16 animate-pulse">
          <div className="h-80 bg-[#F4F7FB] rounded-xl" />
          <div className="h-6 bg-[#F4F7FB] rounded w-1/2 mt-6" />
          <div className="h-4 bg-[#F4F7FB] rounded w-1/3 mt-2" />
        </div>
      </div>
    );

  if (!property)
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 py-24 text-center text-[#1F2A44]">
          Property not found.
        </div>
        <Footer />
      </div>
    );

  const title = (lang === "hi" && property.title_hi) || property.title_en;
  const desc =
    (lang === "hi" && property.description_hi) || property.description_en;
  const images = (property.image_paths || []).map(resolveImageUrl);
  const whatsapp = `https://wa.me/919203809569?text=${encodeURIComponent(
    `Richcube Enquiry — ${property.title_en}`
  )}`;

  const spec = (icon, label, value) =>
    value ? (
      <div className="flex items-start gap-3 py-3 border-b border-[#EBEFF5] last:border-b-0">
        <div className="w-9 h-9 rounded-lg bg-[#F4F7FB] text-[#C9A84C] flex items-center justify-center shrink-0">
          {icon}
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-[#334155]">
            {label}
          </div>
          <div className="text-[#0B2545] font-semibold mt-0.5">{value}</div>
        </div>
      </div>
    ) : null;

  return (
    <div className="min-h-screen bg-white text-[#0B2545] font-sans">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <Link
          to="/properties"
          data-testid="back-link"
          className="inline-flex items-center gap-1.5 text-sm text-[#1F2A44] hover:text-[#0B2545]"
        >
          <ArrowLeft size={14} />
          {t.detail.back}
        </Link>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6 grid grid-cols-1 lg:grid-cols-5 gap-10">
        {/* Left — gallery + content */}
        <div className="lg:col-span-3">
          <div className="relative aspect-[16/10] rounded-xl overflow-hidden bg-[#F4F7FB]" data-testid="detail-gallery">
            {images[activeImg] ? (
              <img
                src={images[activeImg]}
                alt={title}
                className="w-full h-full object-cover"
              />
            ) : null}

            <div className="absolute top-4 left-4 flex flex-col gap-2">
              {property.is_assured_return && (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-[11px] font-semibold uppercase bg-[#FEF9C3] text-[#854D0E] border border-[#C9A84C]/60">
                  <BadgeCheck size={12} />
                  {t.badges.assured}
                  {property.assured_return_pct ? ` · ${property.assured_return_pct}%` : ""}
                </span>
              )}
              {property.is_exclusive && (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-[11px] font-semibold uppercase bg-[#0B2545] text-[#C9A84C]">
                  <Sparkles size={12} />
                  {t.badges.exclusive}
                </span>
              )}
            </div>
          </div>

          {images.length > 1 && (
            <div className="mt-3 flex gap-2 overflow-x-auto">
              {images.map((src, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(i)}
                  data-testid={`gallery-thumb-${i}`}
                  className={`relative shrink-0 w-24 h-16 rounded-md overflow-hidden border transition ${
                    i === activeImg
                      ? "border-[#C9A84C] ring-2 ring-[#C9A84C]/40"
                      : "border-[#EBEFF5] opacity-80 hover:opacity-100"
                  }`}
                >
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}

          <h1 className="mt-8 font-serif text-3xl md:text-4xl text-[#0B2545] rc-heading leading-tight">
            {title}
          </h1>
          <div className="mt-2 inline-flex items-center text-[#1F2A44] gap-1.5">
            <MapPin size={14} className="text-[#C9A84C]" />
            <span>
              {property.locality ? `${property.locality}, ` : ""}
              {property.city}
            </span>
            <span className="mx-2 text-[#C9A84C]">·</span>
            <span className="uppercase tracking-wider text-xs">
              {property.property_type}
            </span>
            <span className="mx-2 text-[#C9A84C]">·</span>
            <span className="uppercase tracking-wider text-xs">
              {property.listing_type === "rent" ? t.search.rent : t.search.buy}
            </span>
          </div>
          <RentalBadge property={property} />

          <div className="mt-8">
            <AssuredRentalBox units={siblings || [property]} highlightId={property.id} />
          </div>

          <div className="mt-8">
            <h2 className="font-serif text-xl text-[#0B2545]">{t.detail.overview}</h2>
            <p className="mt-3 text-[#1F2A44] leading-relaxed whitespace-pre-line">
              {desc}
            </p>
          </div>

          {property.amenities?.length > 0 && (
            <div className="mt-10">
              <h2 className="font-serif text-xl text-[#0B2545]">
                {t.detail.amenities}
              </h2>
              <div className="mt-4 flex flex-wrap gap-2">
                {property.amenities.map((a) => (
                  <span
                    key={a}
                    data-testid={`amenity-${a}`}
                    className="px-3 py-1.5 rounded-full bg-[#F4F7FB] text-[#0B2545] text-xs tracking-wide border border-[#EBEFF5]"
                  >
                    {a}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="mt-10">
            <h2 className="font-serif text-xl text-[#0B2545]">{t.detail.specs}</h2>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-x-10">
              {spec(<HomeIcon size={16} />, t.detail.type, property.property_type)}
              {spec(<MapPin size={16} />, t.detail.locality, property.locality)}
              {spec(<Ruler size={16} />, t.detail.area, property.sqft ? `${property.sqft.toLocaleString()} sqft` : null)}
              {spec(<BedDouble size={16} />, t.detail.bedrooms, property.bhk)}
              {spec(<Sofa size={16} />, t.detail.furnishing, property.furnishing)}
              {spec(<Compass size={16} />, t.detail.facing, property.facing)}
              {spec(<KeyRound size={16} />, t.detail.ownership, property.ownership)}
            </div>
          </div>
        </div>

        {/* Right — sticky enquiry */}
        <aside className="lg:col-span-2">
          <div className="lg:sticky lg:top-24 space-y-6">
            <div className="rounded-2xl border-2 border-[#0B2545] bg-white shadow-[0_30px_60px_-30px_rgba(11,37,69,0.5)] p-6 rc-card-featured">
              <div className="text-[11px] uppercase tracking-[0.22em] text-[#1F2A44] font-bold">
                Investment
              </div>
              <div className="mt-1 rc-price-gold text-3xl font-black tracking-tight">
                Price on Request
              </div>
              {property.is_assured_return && property.assured_return_pct && (
                <div className="mt-5 p-4 rounded-xl bg-[#FFFDF5] border border-[#DFBF65]">
                  <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-[#854D0E] font-semibold">
                    <TrendingUp size={14} />
                    {t.detail.assured_block}
                  </div>
                  <div className="mt-2 font-serif text-2xl text-[#0B2545]">
                    {property.assured_return_pct}%{" "}
                    <span className="text-sm font-sans text-[#1F2A44]">p.a.</span>
                  </div>
                  <p className="mt-2 text-xs text-[#1F2A44] leading-relaxed">
                    {t.detail.assured_desc}
                  </p>
                </div>
              )}

              <Button
                onClick={() => setEnquiryOpen(true)}
                data-testid="detail-enquire-btn"
                className="mt-6 w-full h-12 bg-[#0B2545] text-[#C9A84C] hover:bg-[#133763] hover:text-[#E9D58F] font-semibold tracking-wider uppercase text-xs"
              >
                {t.detail.enquire_now}
              </Button>
              <a
                href={whatsapp}
                target="_blank"
                rel="noreferrer noopener"
                data-testid="detail-whatsapp-btn"
                className="mt-3 w-full h-12 inline-flex items-center justify-center gap-2 rounded-md border border-[#25D366] text-[#1EAE52] hover:bg-[#25D366] hover:text-white font-semibold tracking-wider uppercase text-xs transition-colors"
              >
                <MessageCircle size={16} />
                {t.enquiry.whatsapp_cta}
              </a>
            </div>

            <div className="rounded-2xl border border-[#EBEFF5] bg-[#F4F7FB] p-6 text-sm text-[#1F2A44]">
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#0B2545] font-semibold">
                {t.footer.about}
              </div>
              <p className="mt-2 leading-relaxed">{t.footer.about_text}</p>
            </div>
          </div>
        </aside>
      </div>

      <Footer />
      <EnquiryModal
        property={property}
        open={enquiryOpen}
        onClose={() => setEnquiryOpen(false)}
      />
    </div>
  );
}
